import { create } from 'zustand';
import { BoundingBoxType, CellIdType, CellsBoundingBoxesMapType, RowIdType, RowsBoundingBoxesMapType } from '../types';
import { useShallow } from 'zustand/react/shallow';

// Define the store type for managing rows' bounding boxes
type BoundingBoxesStoreType = {
    // Map storing bounding box information for rows
    rowsBoundingBoxesMap: RowsBoundingBoxesMapType;

    // Map storing the bounding box information for cells
    cellsBoundingBoxesMap: CellsBoundingBoxesMapType;

    // Method to update the entire rows bounding boxes map
    setRowsBoundingBoxesMap: (rowsBoundingBoxesMap: RowsBoundingBoxesMapType) => void;

    // Method to update the entire cells bounding boxes map
    setCellsBoundingBoxesMap: (cellsBoundingBoxesMap: CellsBoundingBoxesMapType) => void;

    // Method for complete replacement of store state
    setStoreState: (
        rowsBoundingBoxesMap: RowsBoundingBoxesMapType,
        cellsBoundingBoxesMap: CellsBoundingBoxesMapType
    ) => void;
};

// Create a Zustand store for tracking row bounding boxes
const useBoundingBoxesStore = create<BoundingBoxesStoreType>()((set) => ({
    // Initial state with an empty bounding boxes map
    rowsBoundingBoxesMap: {},
    cellsBoundingBoxesMap: {},

    // Method to completely replace the bounding boxes map
    setRowsBoundingBoxesMap: (rowsBoundingBoxesMap) => set(() => ({ rowsBoundingBoxesMap })),
    setCellsBoundingBoxesMap: (cellsBoundingBoxesMap) => set(() => ({ cellsBoundingBoxesMap })),
    setStoreState: (rowsBoundingBoxesMap, cellsBoundingBoxesMap) =>
        set(() => ({ rowsBoundingBoxesMap, cellsBoundingBoxesMap })),
}));

export const useSetBoundingBoxesStoreState = () => useBoundingBoxesStore(useShallow((state) => state.setStoreState));
export const useRowBoundingBox = (rowId: RowIdType) =>
    useBoundingBoxesStore(useShallow((state) => state.rowsBoundingBoxesMap[rowId]));
export const useCellBoundingBox = (cellId: CellIdType | null) =>
    useBoundingBoxesStore(
        useShallow((state) => {
            if (cellId) {
                return state.cellsBoundingBoxesMap[cellId];
            } else {
                return {} as BoundingBoxType;
            }
        })
    );

export const useRowsBoundingBoxes = (rowIds: RowIdType[]) =>
    useBoundingBoxesStore(useShallow((state) => rowIds.map((rowId) => state.rowsBoundingBoxesMap[rowId])));

export default useBoundingBoxesStore;
